export type TouchGestureType =
  | 'tap'
  | 'doubleTap'
  | 'swipeLeft'
  | 'swipeRight'
  | 'swipeUp'
  | 'swipeDown'
  | 'drag';

export type HapticLevel = 'light' | 'medium' | 'strong';

export interface TouchGestureEvent {
  type: TouchGestureType;
  x: number;
  y: number;
  deltaX: number;
  deltaY: number;
  elapsedMs: number;
}

interface TouchPoint {
  x: number;
  y: number;
  time: number;
}

export interface TouchInputAdapterOptions {
  swipeThreshold?: number;
  tapMaxMovement?: number;
  tapMaxDurationMs?: number;
  doubleTapWindowMs?: number;
  emitDragEvents?: boolean;
}

const DEFAULTS: Required<TouchInputAdapterOptions> = {
  swipeThreshold: 28,
  tapMaxMovement: 12,
  tapMaxDurationMs: 280,
  doubleTapWindowMs: 260,
  emitDragEvents: false,
};

export class TouchInputAdapter {
  private readonly options: Required<TouchInputAdapterOptions>;
  private startPoint: TouchPoint | null = null;
  private lastPoint: TouchPoint | null = null;
  private lastTapAt = 0;

  constructor(options?: TouchInputAdapterOptions) {
    this.options = {
      ...DEFAULTS,
      ...options,
    };
  }

  public start(x: number, y: number, time = Date.now()): void {
    const point = { x, y, time };
    this.startPoint = point;
    this.lastPoint = point;
  }

  public move(x: number, y: number, time = Date.now()): TouchGestureEvent[] {
    if (!this.startPoint) {
      return [];
    }

    const events: TouchGestureEvent[] = [];
    const previous = this.lastPoint ?? this.startPoint;
    const deltaFromPreviousX = x - previous.x;
    const deltaFromPreviousY = y - previous.y;

    if (this.options.emitDragEvents) {
      events.push({
        type: 'drag',
        x,
        y,
        deltaX: deltaFromPreviousX,
        deltaY: deltaFromPreviousY,
        elapsedMs: time - this.startPoint.time,
      });
    }

    const deltaFromStartX = x - this.startPoint.x;
    const deltaFromStartY = y - this.startPoint.y;
    const absX = Math.abs(deltaFromStartX);
    const absY = Math.abs(deltaFromStartY);

    if (absX >= this.options.swipeThreshold || absY >= this.options.swipeThreshold) {
      if (absX > absY) {
        events.push({
          type: deltaFromStartX > 0 ? 'swipeRight' : 'swipeLeft',
          x,
          y,
          deltaX: deltaFromStartX,
          deltaY: deltaFromStartY,
          elapsedMs: time - this.startPoint.time,
        });
      } else {
        events.push({
          type: deltaFromStartY > 0 ? 'swipeDown' : 'swipeUp',
          x,
          y,
          deltaX: deltaFromStartX,
          deltaY: deltaFromStartY,
          elapsedMs: time - this.startPoint.time,
        });
      }

      this.startPoint = { x, y, time };
    }

    this.lastPoint = { x, y, time };
    return events;
  }

  public end(x: number, y: number, time = Date.now()): TouchGestureEvent[] {
    if (!this.startPoint) {
      return [];
    }

    const deltaX = x - this.startPoint.x;
    const deltaY = y - this.startPoint.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const elapsedMs = time - this.startPoint.time;
    const events: TouchGestureEvent[] = [];

    if (
      absX <= this.options.tapMaxMovement &&
      absY <= this.options.tapMaxMovement &&
      elapsedMs <= this.options.tapMaxDurationMs
    ) {
      if (time - this.lastTapAt <= this.options.doubleTapWindowMs) {
        events.push({ type: 'doubleTap', x, y, deltaX, deltaY, elapsedMs });
        this.lastTapAt = 0;
      } else {
        events.push({ type: 'tap', x, y, deltaX, deltaY, elapsedMs });
        this.lastTapAt = time;
      }
    }

    this.cancel();
    return events;
  }

  public cancel(): void {
    this.startPoint = null;
    this.lastPoint = null;
  }
}

export function triggerHapticFeedback(level: HapticLevel): void {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') {
    return;
  }

  const patternByLevel: Record<HapticLevel, number | number[]> = {
    light: 12,
    medium: 20,
    strong: [25, 12, 25],
  };

  navigator.vibrate(patternByLevel[level]);
}